<script setup>
import GlobalEmitter from '~~/glxp/utils/emitter'

const props = defineProps({
  items: {
    type: Array,
    default: null,
  },
  slug: {
    type: String,
    default: null,
  },
})

function getItemType(item) {
  if (item.audio) {
    return 'audio'
  } else if (item.video) {
    return 'video'
  } else if (item.image) {
    return 'image'
  } else if (item.isQuote) {
    return 'quote'
  }
  return 'text'
}

const itemsWithTypes = computed(() =>
  props.items?.map((item) => ({ ...item, type: getItemType(item) }))
)

const parentIsRevealed = ref(false)
function onParentReveal() {
  parentIsRevealed.value = true
}

function onParentUnreveal() {
  parentIsRevealed.value = false
}

onMounted(() => {
  GlobalEmitter.on(`reveal:${props.slug}`, onParentReveal)
  GlobalEmitter.on(`unreveal:${props.slug}`, onParentUnreveal)
})

onBeforeUnmount(() => {
  GlobalEmitter.off(`reveal:${props.slug}`, onParentReveal)
  GlobalEmitter.off(`unreveal:${props.slug}`, onParentUnreveal)
})
</script>

<template>
  <div class="home-gallery">
    <div
      v-for="(item, index) in itemsWithTypes"
      :key="item.slug"
      class="home-gallery__item"
      :class="[
        `home-gallery__item--${item.slug}`,
        `home-gallery__item--type-${item.type}`,
        `home-gallery__item--index-${index}`,
      ]"
    >
      <template v-if="item.type === 'text' || item.type === 'quote'">
        <SplitTextBlock
          v-if="item.title && item.showTitle"
          class="home-gallery__item-title text__copy-large"
        >
          {{ item.title }}
        </SplitTextBlock>
        <ContentfulRichText
          v-if="item.copy"
          :class="{ text__quote: item.type === 'quote' }"
          size="small"
          :animateManual="index !== 0"
          split
          :sectionSlug="slug"
          :json="item.copy.json"
        />
        <TextAnimation
          v-if="item.attribution"
          class="home-gallery__item-attribution text__eyebrow-medium"
          :parentIsRevealed="parentIsRevealed"
          >{{ item.attribution }}
        </TextAnimation>
      </template>
      <template v-else-if="item.type === 'image'">
        <figure>
          <AnimatedImage
            v-if="item.image"
            class="home-gallery__item-image"
            :media="item.image"
            mediaKey="large"
            :parentIsRevealed="parentIsRevealed"
            animate
          />
          <TextAnimation
            v-if="item.title && item.showTitle"
            tag="figcaption"
            class="home-gallery__item-title text__eyebrow-medium"
            :parentIsRevealed="parentIsRevealed"
          >
            {{ item.title }}
          </TextAnimation>
        </figure>
      </template>
      <template v-else-if="item.type === 'video' || item.type === 'audio'">
        <figure>
          <AnimatedImage
            v-if="item.image"
            class="home-gallery__item-image"
            :media="item.image"
            mediaKey="large"
            :parentIsRevealed="parentIsRevealed"
            animate
          />
          <TextAnimation
            v-if="item.secondaryText"
            class="home-gallery__item-title text__eyebrow-medium"
            tag="figcaption"
            :parentIsRevealed="parentIsRevealed"
          >
            {{ item.secondaryText }}
          </TextAnimation>
        </figure>
        <CardVideo
          v-if="item.videoCard"
          :video-gtm-class="item.videoCard.gtmClass"
          :thumbnail="item.videoCard.thumbnail.url"
          :title="item.videoCard.title"
          :video="item.videoCard.video"
          :copy="item.videoCard.copy"
        />
        <CardAudio
          v-if="item.audio"
          :audio-gtm-class="item.audioGtmClass"
          :thumbnail="item.secondaryImage?.[0].src"
          :title="item.title"
          :audio="item.audio"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use 'sass:math';

@import 'styles/utils/functions';
@import 'styles/vendors/sass-mq/mq';

.home-gallery {
  display: flex;
  flex-direction: column;

  .page-home & {
    padding-bottom: 25vh;
    @include mq($from: 'l') using ($from) {
      padding-bottom: 50vh;
    }
  }

  &__item {
    width: gs(7, 6, 'xxxs');
    margin-bottom: 80px;

    @include mq($until: 'l') using ($until) {
      margin-bottom: 70px;
    }

    &-title {
      display: block;
      text-align: center;
      margin: 16px auto 0;
      color: $brand-white;

      @include mq($from: 'l') using ($from) {
        margin: 32px auto 0;
      }
    }
    &-attribution {
      display: block;
      margin-top: 38px;
    }

    &-image {
      @include mq($until: 'l') using ($until) {
        width: gs(5, 4, 'xxxs');

        + .home-gallery__item-title {
          width: gs(5, 4, 'xxxs');
          margin-left: 0;
        }
      }
    }

    // specific types
    &--type-text {
      .home-gallery__item-title {
        text-align: left;
        margin-bottom: 24px;
      }
    }

    &--type-quote {
      position: relative;
      margin-left: gs(1, 1, 'xxxs');
      width: gs(5, 4, 'xxxs');

      &::before {
        content: '“';
        font-size: 36px;
        font-weight: $font-weight-1G;
        line-height: math.div(90, 36);
        letter-spacing: -0.01em;
        color: $brand-blue;
        border: solid 1.5px rgba($brand-blue, 0.1);
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: baseline;
        width: 76px;
        height: 76px;
        position: absolute;
        pointer-events: none;
        top: -39px;
        left: -59px;

        @include mq($from: 'l') using ($from) {
          font-size: 30px;
          line-height: math.div(110, 30);
          width: 100px;
          height: 100px;
          top: -71px;
          left: -79px;
        }
      }
      &::after {
        content: '';
        border-radius: 50%;
        border: solid 1.5px rgba($brand-blue, 0.05);
        position: absolute;
        width: 88px;
        height: 88px;
        top: -45px;
        left: -65px;

        @include mq($from: 'l') using ($from) {
          width: 118px;
          height: 118px;
          top: -80px;
          left: -88px;
        }
      }
    }

    &--type-video {
      margin-bottom: 40px;

      @include mq($until: 'l') using ($until) {
        margin-bottom: 125px;
        .card-content {
          width: gs(5, 4, 'xxxs');
          margin-left: auto;
          margin-top: -60px;
        }
      }
    }

    &--type-audio {
      position: relative;
      margin-top: 110px;
      margin-left: gs(1, 1, 'xxxs');
      width: gs(5, 4, 'xxxs');

      @include mq($until: 'l') using ($until) {
        margin-top: 80px;
      }

      .card-content {
        position: absolute;
        top: 0;
        // transform: translateY(-50%);
        left: calc(gs(1, 1, 'xxxs') * -1);

        @include mq($until: 'l') using ($until) {
          width: gs(5, 4, 'xxxs');
        }
      }
    }

    @include mq($until: 'l') using ($until) {
      &--intro {
        order: 0;
      }
      &--take-off-and-air-launch {
        order: 1;
      }
      &--quote {
        order: 2;
      }
      &--crew-image {
        order: 3;
      }
      &--podcast {
        order: 4;
      }
      &--colin-sirisha-unity-22 {
        display: none;
      }
      &--zero-g {
        display: none;
      }

      &--index-2,
      &--index-4 {
        .home-gallery__item-image,
        .home-gallery__item-title {
          margin-left: auto;
          margin-right: 0;
        }
      }
    }
  }

  @include mq($from: 'l') using ($from) {
    display: grid;
    grid-template-columns: repeat(24, 1fr);
    column-gap: 24px;

    &__item {
      grid-column-end: span 5;
      width: auto;
      margin-bottom: 0;

      // specific types
      &--type-quote {
        margin-left: 0;

        .home-gallery__item-title {
          text-align: center;
        }
      }

      &--type-audio {
        .card-content {
          position: absolute;
          left: 70%;
          top: 81px;
        }
      }

      &--type-video {
        .card-content {
          transform: none;
        }
      }
    }
  }
}

// Home, specific index
@include mq($until: 'l') using ($until) {
  .page-home .home-gallery__item {
    &--index-2 {
      position: relative;
      margin-top: 85px;

      figure {
        padding-top: 45px;
      }

      .card-content {
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    &--index-3 {
      .card-content {
        transform: translateY(-75%);
      }
    }
  }
}

@include mq($from: 'l') using ($from) {
  // Home, specific indices
  .page-home .home-gallery__item {
    &--index-0 {
      grid-column: 6 / span 5;
      margin-top: 244px;
    }

    &--index-1 {
      grid-column: 14 / span 9;
      margin-top: 194px;
    }

    &--index-2 {
      grid-column: 2 / span 9;
      position: relative;
      margin-top: calc(-100% + 193px);

      .home-gallery__item-image {
        width: 88.2%;
        margin-left: auto;
      }

      .card-content {
        position: absolute;
        left: 0;
        bottom: 77px;
      }
    }

    &--index-3 {
      grid-column: 5 / span 13;
      margin: 191px 0 0;
      position: relative;
    }

    &--index-4 {
      grid-column: 3 / span 9;
      margin-top: 200px;
    }

    &--index-5 {
      grid-column: 15 / span 7;
      margin-top: 256px;
    }

    &--index-6 {
      grid-column: 15 / span 8;
      margin-top: calc(-100% + 125px);
    }
  }

  .page-spaceflight-experience .home-gallery__item {
    &--index-0 {
      grid-column: 6 / span 4;
      margin-top: 150px;
    }

    &--index-1 {
      grid-column: 14 / span 9;
      grid-row: 1;
      margin-top: 110px;
    }

    &--index-2 {
      grid-column: 3 / span 8;
      grid-row: 2;
      margin-top: -50%;
    }
  }

  .page-spaceport .home-gallery__item {
    &--index-0 {
      grid-column: 6 / span 7;
      margin-top: 200px;
    }
    &--index-1 {
      grid-column: 16 / span 7;
      margin-top: 140px;
    }
    &--index-2 {
      grid-column: 3 / span 9;
      margin-top: -110px;
    }
    &--index-3 {
      grid-column: 14 / span 6;
      margin-top: 204px;
    }

    &--index-4 {
      grid-column: 13 / span 10;
      margin-top: 140px;
    }
  }
}
</style>
